/* Reader Font Bar — Feb-2026.
 *
 * Compact toolbar that lets signed-in readers override the author's
 * publishing style. Preferences persist to `/api/reader/preferences`;
 * anonymous readers fall back to localStorage so their pick sticks
 * across sessions on the same device.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Type, Accessibility } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const LOCAL_KEY = 'cr_reader_prefs_v1';

const BODY_FONT_CHOICES = [
  { id: 'garamond',    label: 'Garamond',    stack: "'EB Garamond', Garamond, Georgia, serif" },
  { id: 'baskerville', label: 'Baskerville', stack: "'Libre Baskerville', Baskerville, Georgia, serif" },
  { id: 'palatino',    label: 'Palatino',    stack: "Palatino, 'Palatino Linotype', 'Book Antiqua', Georgia, serif" },
  { id: 'times',       label: 'Times',       stack: "'Times New Roman', Times, serif" },
];

/**
 * @param {object} props
 * @param {string} props.defaultCss  The book's baseline CSS from the author.
 * @param {(css:string)=>void} props.onCssChange
 */
export default function ReaderFontBar({ defaultCss = '', onCssChange, signedIn = false }) {
  const [prefs, setPrefs] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(LOCAL_KEY) || '{}');
    } catch { return {}; }
  });
  const [saving, setSaving] = useState(false);

  // On mount: pull server prefs when logged in, override local copy.
  useEffect(() => {
    if (!signedIn) return;
    axios.get(`${API}/reader/preferences`, { withCredentials: true })
      .then((r) => {
        if (r.data?.preferences && Object.keys(r.data.preferences).length) {
          setPrefs(r.data.preferences);
        }
      })
      .catch(() => {}); // Anonymous or expired session — silent fall-back.
  }, [signedIn]);

  // On prefs change: emit the resolved CSS.
  useEffect(() => {
    let css = defaultCss || '';
    const hit = BODY_FONT_CHOICES.find((f) => f.id === prefs.body_font);
    if (hit) {
      css += `\n.pub-body{font-family:${hit.stack} !important;}`;
    }
    if (prefs.body_size_pt) {
      css += `\n.pub-body{font-size:${prefs.body_size_pt}pt !important;}`;
    }
    if (prefs.leading) {
      css += `\n.pub-body{line-height:${prefs.leading} !important;}`;
    }
    if (prefs.dyslexic) {
      css += "\n.pub-body{font-family:'OpenDyslexic','Comic Neue','Verdana',sans-serif !important;letter-spacing:0.02em !important;}";
    }
    onCssChange?.(css);
    try { localStorage.setItem(LOCAL_KEY, JSON.stringify(prefs)); } catch {}
  }, [prefs, defaultCss, onCssChange]);

  const persistToServer = useCallback((next) => {
    if (!signedIn) return;
    setSaving(true);
    axios.put(`${API}/reader/preferences`, next, { withCredentials: true })
      .catch(() => {})
      .finally(() => setSaving(false));
  }, [signedIn]);

  const patch = (k, v) => {
    const next = { ...prefs, [k]: v };
    if (v === '' || v === null || v === undefined) delete next[k];
    setPrefs(next);
    persistToServer(next);
  };

  return (
    <div className="mt-6 flex flex-wrap items-center gap-2 rounded-md border border-border bg-panel-header/30 px-3 py-2"
         data-testid="reader-font-bar">
      <div className="flex items-center gap-1 text-[13px] uppercase tracking-wider text-muted-foreground">
        <Type className="h-3 w-3" /> Reading font
      </div>
      <select value={prefs.body_font || ''}
              onChange={(e) => patch('body_font', e.target.value || null)}
              data-testid="reader-font-body-font"
              className="rounded border border-border bg-background px-2 py-1 text-[13.5px]">
        <option value="">Author's default</option>
        {BODY_FONT_CHOICES.map((f) => (
          <option key={f.id} value={f.id}>{f.label}</option>
        ))}
      </select>
      <select value={prefs.body_size_pt || ''}
              onChange={(e) => patch('body_size_pt', parseInt(e.target.value, 10) || null)}
              data-testid="reader-font-size"
              className="rounded border border-border bg-background px-2 py-1 text-[13.5px]">
        <option value="">Default size</option>
        {[10, 11, 12, 14, 16, 18].map((s) => (
          <option key={s} value={s}>{s}pt</option>
        ))}
      </select>
      <select value={prefs.leading || ''}
              onChange={(e) => patch('leading', parseFloat(e.target.value) || null)}
              data-testid="reader-font-leading"
              className="rounded border border-border bg-background px-2 py-1 text-[13.5px]">
        <option value="">Default spacing</option>
        <option value="1.2">Tight</option>
        <option value="1.35">Normal</option>
        <option value="1.6">Airy</option>
        <option value="1.9">Wide</option>
      </select>
      <label className="ml-1 flex items-center gap-1 text-[13.5px]">
        <input type="checkbox" checked={!!prefs.dyslexic}
               onChange={(e) => patch('dyslexic', e.target.checked)}
               data-testid="reader-font-dyslexic" />
        <Accessibility className="h-3 w-3" /> Dyslexic-friendly
      </label>
      {signedIn ? (
        <span className="ml-auto text-[13px] text-muted-foreground"
              data-testid="reader-font-saved">
          {saving ? 'Saving…' : 'Saved to your library'}
        </span>
      ) : (
        <span className="ml-auto text-[13px] text-muted-foreground"
              data-testid="reader-font-local">
          Saved on this device — sign in to sync
        </span>
      )}
    </div>
  );
}
